import React, { forwardRef, useImperativeHandle, useRef } from "react";
import defaultSgtradexLogo from "@/assets/svgs/logo-sgtradex.svg";
import { DocumentDetailsSection } from "../../DocumentDetailsSection";
import { EndorsementChainSection } from "../../EndorsementChainSection";
import { ProcessPDF, convertSvgUrlToBase64 } from "@/utils/pdf-download";
import { pdfHeader } from "@/utils/pdfHeader";
import PropTypes from 'prop-types';
import InformationCircle from '@/assets/svgs/information-icon.png';

const SectionModelTerms = (document) => {
  const { etd_model_terms: modelTerms } = document;
  return (
    <div
      style={{ fontWeight: 500, fontSize: "14px", color: "#484B53" }}
      dangerouslySetInnerHTML={{ __html: modelTerms?.etd_model_terms }}
    />
  );
};

const SectionNoticeTerms = (document) => {
  const { etd_model_terms: modelTerms } = document;
  const noticeText = typeof modelTerms?.etd_notice === "string" ? modelTerms.etd_notice : "";
  const modelTermsNotice = noticeText.replace(/font-family:\s*'Avenir',\s*sans-serif;/g, "font-family: 'Arial';");
  return (
    <div
      data-testid="section-notice"
      style={{
        borderRadius: "12px",
        backgroundColor: "#FFE7FB",
      }}
    >
      <div style={{ display: "flex", padding: "16px 16px 0 16px" }}>
        <div style={{ marginRight: '8px', marginTop: '1px' }}>
          <img 
            src={InformationCircle}
            alt="infoicon"
            style={{
              width: "16px",
              height: "16px",
              maxWidth: "none",
              maxHeight: "none",
            }}
          />
        </div>
        <div style={{ fontWeight: 800, fontSize: "12px", fontFamily: "Arial" }}>
          SGTraDex eBL Additional Terms and Conditions:
        </div>
      </div>
      <div
        style={{ fontWeight: 500, fontSize: "14px", color: "#484B53" }}
        dangerouslySetInnerHTML={{ __html: modelTermsNotice }}
      />
    </div>
  );
};

const Section1 = (logo, document) => {
  const { etd_bl_type: blType, etd_reference_number: refNumber, etd_type: docType } = document;
  return (
    <>
      <div style={{ display: "flex" }}>
        <div style={{ width: "50%", border: "1px solid black", minHeight: "6rem", borderBottom: "0px", borderRight: "0px" }}>
          <div style={{ padding: "1px", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            {!logo.includes(".svg") && <img
              data-testid="logo"
              style={{ width: "75px", height: "auto", backgroundColor: "red" }}
              src={logo ? `https://blob.sgtradex.io/${logo}` : defaultSgtradexLogo}
              onError={(e) => {
                e.currentTarget.src = defaultSgtradexLogo;
              }}
              alt="fallback logo"
            />}
          </div>
        </div>

        <div style={{ width: "50%", border: "1px solid black", borderBottom: "0px" }}>
          <div style={{ padding: "8px" }}>
            <p>
              <strong>
                OCEAN BILL OF LADING FOR OCEAN TRANSPORT OR MULTIMODAL TRANSPORT
              </strong>
            </p>
          </div>
        </div>
      </div>
      <div style={{ display: "flex" }}>
        <div data-testid="etd-bl-type" style={{ width: "50%", border: "1px solid black", borderRight: "0px" }}>
          <div style={{ padding: "8px" }}>
            Bill Of Lading Type
            <p>{blType}</p>
          </div>
        </div>

        <div style={{ width: "50%", display: "flex" }}>
          <div data-testid="etd-document-type" style={{ flex: 1, border: "1px solid black" }}>
            <div style={{ padding: "8px" }}>
              Document Type
              <p>{docType}</p>
            </div>
          </div>
          <div data-testid="etd-reference-number" style={{ flex: 1, border: "1px solid black", borderLeft: "0px" }}>
            <div style={{ padding: "8px" }}>
              REFERENCE NUMBER
              <p>{refNumber}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const ExternalTransportDocumentTemplate = forwardRef(({ document, logo, endorsementChainLists = undefined, issuer, owner, holder, created, tokenRegistryVersion, tokenRegistryAddress, merkleRoot, fileName }, ref) => {
  const { etd_bl_type, etd_reference_number, etd_type, etd_model_terms, etd_transport_doc_pdf } = document;

  const dataElementRef = useRef();
  const modelTermsRef = useRef();
  const endorsementChainRef = useRef();

  useImperativeHandle(ref, () => {
    return {
      handleClick: handleClickChild,
    };
  }, []);

  const handleClickChild = async (onComplete) => {
    console.log("PDF downloading started");
    let base64Image = "";
    if (logo.includes(".svg")) {
      await convertSvgUrlToBase64(logo).then((base64StringInternal) => base64Image = base64StringInternal);
    }
    const processPDFDownload = new ProcessPDF({
      staticHeader: (pdf) => pdfHeader(
        pdf,
        "E-Bill of Lading",
        merkleRoot
      ),
      orientation: "p",
      format: [794, 1123],
      marginX: 20,
      marginTop: 120,
      marginBottom: 100,
      fileName: `${fileName.split(".")[0]}.pdf`
    });

    const pdfSections = [
      { data: dataElementRef.current, type: "html" },
      etd_transport_doc_pdf?.attachments?.[0]?.file_content && { data: etd_transport_doc_pdf.attachments[0].file_content, type: "file" },
      etd_model_terms && { data: modelTermsRef.current, type: "html" },
      endorsementChainLists && endorsementChainLists.length > 0 && { data: endorsementChainRef.current, type: "html" }
    ].filter(Boolean);

    await processPDFDownload.downloadPDF(pdfSections, base64Image, 490).then((response) => {
      console.log("response", response);
    }).catch((error) => {
      console.log("error", error);
    }).finally(() => {
      onComplete?.(); // Notify parent to hide loader
    });
  }

  return (<>
    {/* Document Details, Notice and Data Element Details */}
    <div ref={dataElementRef}>
      <div style={{ border: "1px solid #D7DAE3", borderRadius: "4px", backgroundColor: "#FFFFFF" }}>
        <div style={{ display: "flex", flexDirection: "column", padding: "16px" }}>
          <DocumentDetailsSection
            issuer={issuer}
            owner={owner}
            holder={holder}
            created={created}
            tokenRegistryVersion={tokenRegistryVersion}
            tokenRegistryAddress={tokenRegistryAddress}
          />
        </div>
      </div>
      <div style={{ border: "1px solid #D7DAE3", borderRadius: "4px", marginTop: "32px", backgroundColor: "#FFFFFF" }}>
        <div style={{ fontWeight: 800, fontSize: "12px", fontFamily: "Arial", borderBottom: "1px solid #D7DAE3", padding: "16px" }}>{"E-Bill of Lading"}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "32px", padding: "16px" }}>
          {etd_model_terms && <div style={{ marginBottom: "16px" }}>{SectionNoticeTerms({ etd_model_terms })}</div>}
          <div style={{ marginBottom: "16px" }}>{Section1(logo, { etd_bl_type, etd_reference_number, etd_type })}</div>
        </div>
      </div>
    </div>

    {/* Model Terms */}
    {etd_model_terms && <div ref={modelTermsRef} style={{ border: "1px solid #D7DAE3", borderRadius: "4px", marginTop: "8px", backgroundColor: "#FFFFFF" }}>
      <div style={{ fontWeight: 800, fontSize: "12px", fontFamily: "Arial", borderBottom: "1px solid #D7DAE3", padding: "16px" }}>{"SGTraDex eBL Additional Terms and Conditions (continued)"}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: "64px", padding: "16px" }}>
        <div style={{ marginBottom: "16px" }}>{SectionModelTerms({ etd_model_terms })}</div>
      </div>
    </div>}

    {/* Endorsement Chain */}
    <div ref={endorsementChainRef} style={{ border: "1px solid #D7DAE3", borderRadius: "4px", marginTop: "8px", backgroundColor: "#FFFFFF" }}>
      <div style={{ fontWeight: 800, fontSize: "12px", fontFamily: "Arial", borderBottom: "1px solid #D7DAE3", padding: "16px" }}>{"Endorsement Chain"}</div>
      <EndorsementChainSection endorsementChainLists={endorsementChainLists} />
    </div>

  </>);

});

ExternalTransportDocumentTemplate.propTypes = {
  document: PropTypes.object,
  issuer: PropTypes.string,
  owner: PropTypes.string,
  holder: PropTypes.string,
  created: PropTypes.string,
  tokenRegistryVersion: PropTypes.string,
  tokenRegistryAddress: PropTypes.string,
  logo: PropTypes.string,
  endorsementChainLists: PropTypes.array,
  merkleRoot: PropTypes.string,
  fileName: PropTypes.string
};